import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Loader, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import TextAreaField from 'components/TextAreaField';
import { useTranslation } from 'react-i18next';
import validations from 'validations';
import { SOFT_SIGNALS, SoftSignal, SoftSignalPayload } from 'models/Lead';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import mixpanelService from 'services/mixpanelService';
import { SoftSignalInitiatedEventProps } from 'models/MixpanelEvents';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';

import './UnsureSignalModal.styles.scss';

type UnsureSignalModalProps = {
  className?: string;
  dataRoomNodeId: string;
  dataRoomId: string;
  dealRoomId: string;
  onSuccess: (lastSoftSignal: SoftSignal, withdrawInterest?: boolean) => void;
} & ModalProps;

const UnsureSignalModal: React.ForwardRefRenderFunction<
  ModalRef,
  UnsureSignalModalProps
> = (props, ref) => {
  const {
    className,
    dataRoomNodeId,
    dataRoomId,
    dealRoomId,
    onSuccess,
    ...rest
  } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('unsure-signal-modal', className);

  const { t } = useTranslation();
  const getMixpanelProps = useGetMixpanelProps();

  const onSubmit = useCallback(
    async ({ message }: SoftSignalPayload) => {
      const softSignal = SOFT_SIGNALS.UNSURE;

      try {
        mixpanelService.softSignalInitiated(
          getMixpanelProps({
            dataRoomId,
            documentId: dataRoomNodeId,
            dealId: dealRoomId,
            reaction: t('notSure'),
          }) as SoftSignalInitiatedEventProps,
        );

        await api.dataRoom.nodes.softSignals(dataRoomNodeId, {
          message,
          softSignal,
        });

        popSuccess(t('softSignalSuccessMessage'));

        onSuccess(softSignal);

        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      dataRoomNodeId,
      modal,
      dataRoomId,
      dealRoomId,
      getMixpanelProps,
      onSuccess,
      popServerError,
      popSuccess,
      t,
    ],
  );

  return (
    <Modal ref={modalRef} className={classes} title={t('tellUsMore')} {...rest}>
      <Form
        onSubmit={onSubmit}
        render={({ submitting, invalid, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            {submitting && <Loader />}

            <Field
              name="message"
              component={TextAreaField}
              label={t('haveSpecificQuestion')}
              rows={4}
              required
              validate={validations.required()}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={submitting || invalid}>
                {t('send')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(UnsureSignalModal);
